import { pgTable, text, timestamp, uuid, boolean, integer, json, pgEnum, index } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';

export const users = pgTable('users', {
  id: uuid('id').primaryKey().defaultRandom(),
  clerkId: text('clerk_id').notNull().unique(),
  email: text('email').notNull().unique(),
  firstName: text('first_name'),
  lastName: text('last_name'),
  imageUrl: text('image_url'),
  isActive: boolean('is_active').default(true),
  isAdmin: boolean('is_admin').default(false),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

export const currencyEnum = pgEnum('currency_enum', ['USD', 'UYU']);
export const payTypeEnum = pgEnum('pay_type_enum', ['ANUAL', 'MENSUAL', 'UNICO', 'REFERIDO']);
export const resourcesEnum = pgEnum('resources_enum', ['COMPARTIDOS', 'DEDICADOS']);
export const sitesEnum = pgEnum('sites_enum', ['ONE', 'TWO', 'MULTIPLES']);
export const hostingSystemEnum = pgEnum('hosting_system_enum', ['APACHE', 'NGINX', 'OTRO']);
export const securityEnum = pgEnum('security_enum', ['NONE', 'BASIC', 'MEDIUM', 'ENHANCED']);
export const cacheEnum = pgEnum('cache_enum', ['NONE', 'SI', 'DINAMICA', 'OBJETOS', 'DINAMICA_OBJETOS']);
export const imageOptEnum = pgEnum('image_opt_enum', ['NONE', 'SI', 'SMUSH_PRO']);
export const orderStatusEnum = pgEnum('order_status_enum', ['PENDING', 'PAID', 'CANCELLED', 'ACTIVE']);
export const paymentMethodEnum = pgEnum('payment_method_enum', ['PAYPAL', 'PREX', 'TRANSFERENCIA']);

export const hostings = pgTable('hostings', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull(),
  displayName: text('display_name').notNull(),
  mainPhotoUrl: text('main_photo_url'),
  currency: currencyEnum('currency').notNull(),
  price: integer('price').notNull(),
  payType: payTypeEnum('pay_type').notNull(),
  payTypeDisplay: text('pay_type_display'),
  resources: resourcesEnum('resources').notNull(),
  dataCenterSelectable: boolean('data_center_selectable').notNull().default(false),
  sites: sitesEnum('sites').notNull(),
  hostingSystem: hostingSystemEnum('hosting_system').notNull(),
  ssl: boolean('ssl').notNull().default(true),
  spaceGb: integer('space_gb').notNull(),
  emails: integer('emails').notNull().default(0),
  bandwidth: text('bandwidth'),
  backups: boolean('backups').notNull().default(false),
  security: securityEnum('security').notNull(),
  backupsType: text('backups_type'),
  cache: cacheEnum('cache').notNull(),
  imageOptimization: imageOptEnum('image_optimization').notNull(),
  photos: json('photos'),
  resourcesDetail: text('resources_detail'),
  dataCenterDetail: text('data_center_detail'),
  mailsDetail: text('mails_detail'),
  backupsDetail: text('backups_detail'),
  securityDetail: text('security_detail'),
  paypalButtonHtml: text('paypal_button_html'),
  sortOrder: integer('sort_order').notNull().default(0),
  isVisible: boolean('is_visible').notNull().default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type Hosting = typeof hostings.$inferSelect;
export type NewHosting = typeof hostings.$inferInsert;

export const complements = pgTable('complements', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull(),
  displayName: text('display_name').notNull(),
  mainPhotoUrl: text('main_photo_url'),
  currency: currencyEnum('currency').notNull(),
  price: integer('price').notNull(),
  payType: payTypeEnum('pay_type').notNull(),
  payTypeDisplay: text('pay_type_display'),
  description: text('description'),
  paypalButtonHtml: text('paypal_button_html'),
  sortOrder: integer('sort_order').notNull().default(0),
  isVisible: boolean('is_visible').notNull().default(true),
  photos: json('photos'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type Complement = typeof complements.$inferSelect;
export type NewComplement = typeof complements.$inferInsert;

// Tabla de proveedores externos (para servicios externos, dominios, etc.)
export const providers = pgTable('providers', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull().unique(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type Provider = typeof providers.$inferSelect;
export type NewProvider = typeof providers.$inferInsert;

export const orders = pgTable('orders', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  hostingId: uuid('hosting_id').references(() => hostings.id),
  complementId: uuid('complement_id').references(() => complements.id),
  domain: text('domain'),
  status: orderStatusEnum('status').notNull().default('PENDING'),
  quantity: integer('quantity').notNull().default(1),
  lastPaymentMethod: paymentMethodEnum('last_payment_method'),
  notes: text('notes'),
  cpanelUrl: text('cpanel_url'),
  cpanelUser: text('cpanel_user'),
  cpanelPasswordEnc: text('cpanel_password_enc'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type Order = typeof orders.$inferSelect;
export type NewOrder = typeof orders.$inferInsert;

export const externalServices = pgTable('external_services', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  name: text('name').notNull(),
  type: text('type'),
  domain: text('domain'),
  price: integer('price').notNull(),
  currency: currencyEnum('currency').notNull().default('USD'),
  payType: payTypeEnum('pay_type').notNull(),
  startDate: timestamp('start_date'),
  nextPaymentDate: timestamp('next_payment_date'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type ExternalService = typeof externalServices.$inferSelect;
export type NewExternalService = typeof externalServices.$inferInsert;

export const ordersRelations = relations(orders, ({ one }) => ({
  user: one(users, {
    fields: [orders.userId],
    references: [users.id],
  }),
  hosting: one(hostings, {
    fields: [orders.hostingId],
    references: [hostings.id],
  }),
  complement: one(complements, {
    fields: [orders.complementId],
    references: [complements.id],
  }),
}));

export const usersRelations = relations(users, ({ many }) => ({
  orders: many(orders),
  externalServices: many(externalServices),
  notifications: many(notifications),
}));

export const notifications = pgTable('notifications', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  title: text('title').notNull(),
  message: text('message').notNull(),
  isRead: boolean('is_read').notNull().default(false),
  isArchived: boolean('is_archived').notNull().default(false),
  deletedAt: timestamp('deleted_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  userIdIdx: index('notifications_user_id_idx').on(table.userId),
  isReadIdx: index('notifications_is_read_idx').on(table.isRead),
}));

export type Notification = typeof notifications.$inferSelect;
export type NewNotification = typeof notifications.$inferInsert;

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
}));

export const hostingsRelations = relations(hostings, ({ many }) => ({
  orders: many(orders),
}));

export const complementsRelations = relations(complements, ({ many }) => ({
  orders: many(orders),
}));

export const externalServicesRelations = relations(externalServices, ({ one }) => ({
  user: one(users, {
    fields: [externalServices.userId],
    references: [users.id],
  }),
}));

export const requestStatusEnum = pgEnum('request_status_enum', ['PENDING', 'APPROVED', 'REJECTED']);

export const adminRequests = pgTable('admin_requests', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  requestedBy: text('requested_by').notNull(),
  status: requestStatusEnum('status').default('PENDING').notNull(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export type AdminRequest = typeof adminRequests.$inferSelect;
export type NewAdminRequest = typeof adminRequests.$inferInsert;

export const adminRequestsRelations = relations(adminRequests, ({ one }) => ({
  user: one(users, {
    fields: [adminRequests.userId],
    references: [users.id],
  }),
}));
